import Chat from './Chat';
import TrackingMap from './TrackingMap';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import io from 'socket.io-client';

const API_BASE = '/api';

const useDebounce = (fn, delay) => {
  const timerRef = useRef(null);
  return useCallback((...args) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
};

const useCustomerLocation = (userId, activeJobId) => {
  const [locationStatus, setLocationStatus] = useState('idle');
  const [customerLocation, setCustomerLocation] = useState(null);
  const watchIdRef = useRef(null);

  const startWatching = useCallback(() => {
    if (watchIdRef.current != null || !navigator.geolocation) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCustomerLocation(loc);

        if (userId && activeJobId) {
          const token = localStorage.getItem('token');
          fetch(`${API_BASE}/jobs/customer/${userId}/location`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ lat: loc.lat, lng: loc.lng, jobId: activeJobId }),
          }).catch(() => {});
        }
      },
      (err) => console.error('Customer watchPosition error:', err),
      { enableHighAccuracy: true }
    );
  }, [userId, activeJobId]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) { setLocationStatus('denied'); return; }

    const doGet = () => {
      setLocationStatus('requesting');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocationStatus('granted');
          setCustomerLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          startWatching();
        },
        () => setLocationStatus('denied'),
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
      );
    };

    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((r) => r.state === 'denied' ? setLocationStatus('denied') : doGet())
        .catch(() => doGet());
    } else {
      doGet();
    }
  }, [startWatching]);

  useEffect(() => {
    if (!navigator.geolocation || !navigator.permissions) return;
    navigator.permissions.query({ name: 'geolocation' }).then((r) => {
      if (r.state === 'granted') { setLocationStatus('granted'); startWatching(); }
      else if (r.state === 'denied') { setLocationStatus('denied'); }
    }).catch(() => {});
  }, [startWatching]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  return { locationStatus, customerLocation, requestLocation };
};

const LocationPermissionBanner = ({ status, onRequest }) => {
  if (status === 'granted') return null;

  if (status === 'idle') return (
    <div style={{ borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', backgroundColor: '#1a2540', border: '1px solid #3b82f6' }}>
      <div>
        <div style={{ color: '#60a5fa', fontWeight: 'bold', marginBottom: '4px' }}>📍 Share Your Location</div>
        <div style={{ color: '#e5e7eb', fontSize: '13px' }}>Allow location so the driver can track your pickup point in real time.</div>
      </div>
      <button onClick={onRequest} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', whiteSpace: 'nowrap' }}>
        📍 Use My Location
      </button>
    </div>
  );

  if (status === 'requesting') return (
    <div style={{ borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', backgroundColor: '#1a2540', border: '1px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span style={{ fontSize: '20px' }}>⏳</span>
      <div style={{ color: '#93c5fd', fontWeight: 'bold' }}>Waiting for permission… tap Allow in the browser prompt.</div>
    </div>
  );

  if (status === 'denied') return (
    <div style={{ borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', backgroundColor: '#3b0000', border: '1px solid #ef4444' }}>
      <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '4px' }}>🚫 Location Blocked</div>
      <div style={{ color: '#e5e7eb', fontSize: '13px' }}>
        Click the 🔒 lock icon → Site settings → Location → Allow, then refresh.
      </div>
    </div>
  );

  return null;
};

const DriverStatusBar = ({ activeJob }) => {
  if (!activeJob) return null;

  const steps = ['pending', 'assigned', 'picked_up', 'delivered'];
  const currentStep = steps.indexOf(activeJob.status);
  const labels = { pending: 'Pending', assigned: 'Driver Assigned', picked_up: 'On The Way', delivered: 'Delivered' };
  const icons = { pending: '⏳', assigned: '🚚', picked_up: '📦', delivered: '✅' };

  return (
    <div style={{ backgroundColor: '#111827', borderRadius: '10px', padding: '16px', marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '18px', left: '10%', right: '10%', height: '2px', backgroundColor: '#374151', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: '18px', left: '10%', height: '2px', backgroundColor: '#eab308', zIndex: 1, width: `${(currentStep / (steps.length - 1)) * 80}%`, transition: 'width 0.5s ease' }} />
        {steps.map((step, idx) => (
          <div key={step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', zIndex: 2, flex: 1 }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: idx <= currentStep ? '#eab308' : '#374151', fontSize: '16px', transition: 'background-color 0.3s', border: idx === currentStep ? '2px solid white' : 'none' }}>
              {icons[step]}
            </div>
            <div style={{ fontSize: '10px', color: idx <= currentStep ? '#eab308' : '#6b7280', textAlign: 'center', fontWeight: idx === currentStep ? 'bold' : 'normal' }}>
              {labels[step]}
            </div>
          </div>
        ))}
      </div>

      {activeJob.driver_name && activeJob.status !== 'pending' && (
        <div style={{ marginTop: '16px', padding: '10px', backgroundColor: '#1f2937', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#eab308', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'black', fontSize: '16px' }}>
            {activeJob.driver_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{activeJob.driver_name}</div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>Your driver • {activeJob.vehicle_type || 'Van'}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981' }} />
            <span style={{ fontSize: '12px', color: '#10b981' }}>Live tracking</span>
          </div>
        </div>
      )}
    </div>
  );
};

const InventoryRow = ({ label, field, value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #1f2937' }}>
    <span style={{ fontSize: '13px', color: '#d1d5db' }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <button onClick={() => onChange(field, Math.max(0, value - 1))} style={{ width: '28px', height: '28px', borderRadius: '6px', border: 'none', backgroundColor: '#374151', color: 'white', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>−</button>
      <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: 'bold' }}>{value}</span>
      <button onClick={() => onChange(field, value + 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', border: 'none', backgroundColor: '#eab308', color: 'black', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>+</button>
    </div>
  </div>
);

// ==================== BOX SIZE SELECTION COMPONENT ====================
const BoxSizeSelection = ({ selectedSize, onSelect }) => {
  const sizes = [
    { id: 'small', label: '📦 Small', desc: '≤ 68×53×43cm', color: '#60a5fa' },
    { id: 'medium', label: '📦 Medium', desc: '≤ 68×53×43cm', color: '#34d399' },
    { id: 'large', label: '📦 Large', desc: '> 68×53×43cm', color: '#ef4444' },
  ];

  return (
    <div style={{ marginBottom: '10px', padding: '8px 0' }}>
      <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block', marginBottom: '6px' }}>
        📦 Box Size
      </label>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {sizes.map((size) => (
          <button
            key={size.id}
            onClick={() => onSelect(size.id)}
            style={{
              padding: '6px 14px',
              borderRadius: '12px',
              border: selectedSize === size.id ? `2px solid ${size.color}` : '1px solid #374151',
              backgroundColor: selectedSize === size.id ? `rgba(234, 179, 8, 0.15)` : 'transparent',
              color: 'white',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: selectedSize === size.id ? 'bold' : 'normal',
              textAlign: 'center',
              transition: 'all 0.3s ease',
            }}
          >
            <div style={{ fontSize: '14px' }}>{size.label}</div>
            <div style={{ fontSize: '9px', color: selectedSize === size.id ? size.color : '#9ca3af' }}>
              {size.desc}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ==================== VEHICLE SELECTION COMPONENT ====================
const VehicleSelection = ({ selectedVehicle, onSelect }) => {
  const vehicles = [
    { id: 'car', label: '🚗 Private Car', desc: 'Passenger only (up to 4)', icon: '🚗' },
    { id: 'passenger+cargo', label: '🚐 Passenger + Cargo', desc: '2 people + luggage', icon: '🚐' },
    { id: 'bike', label: '🏍️ Bike', desc: 'Small/Medium boxes (≤ 2)', icon: '🏍️' },
    { id: 'van', label: '🚐 Van', desc: 'Furniture & multiple items', icon: '🚐' },
    { id: 'van+trailer', label: '🚛 Van + Trailer', desc: 'Large items & house moves', icon: '🚛' },
  ];

  return (
    <div style={{ marginBottom: '15px' }}>
      <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
        🚗 Vehicle Type
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
        {vehicles.map((vehicle) => (
          <button
            key={vehicle.id}
            onClick={() => onSelect(vehicle.id)}
            style={{
              padding: '12px 10px',
              borderRadius: '10px',
              border: selectedVehicle === vehicle.id ? '2px solid #eab308' : '1px solid #374151',
              backgroundColor: selectedVehicle === vehicle.id ? 'rgba(234, 179, 8, 0.15)' : 'transparent',
              color: 'white',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              if (selectedVehicle !== vehicle.id) {
                e.target.style.borderColor = '#4b5563';
                e.target.style.backgroundColor = 'rgba(255,255,255,0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedVehicle !== vehicle.id) {
                e.target.style.borderColor = '#374151';
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            <div style={{ fontSize: '24px' }}>{vehicle.icon}</div>
            <div style={{ fontSize: '13px', fontWeight: selectedVehicle === vehicle.id ? 'bold' : 'normal', marginTop: '4px' }}>
              {vehicle.label}
            </div>
            <div style={{ fontSize: '10px', color: '#9ca3af' }}>{vehicle.desc}</div>
            {selectedVehicle === vehicle.id && (
              <div style={{ fontSize: '10px', color: '#eab308', marginTop: '4px' }}>
                ✅ Selected
              </div>
            )}
          </button>
        ))}
      </div>
      <div style={{ 
        marginTop: '8px', 
        fontSize: '12px', 
        color: '#60a5fa',
        padding: '8px 12px',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderRadius: '6px',
        border: '1px solid rgba(59, 130, 246, 0.2)'
      }}>
        <i className="fas fa-lightbulb"></i> 
        {selectedVehicle === 'car' && '🚗 Private Car recommended for passenger-only trips'}
        {selectedVehicle === 'passenger+cargo' && '🚐 Passenger + Cargo recommended for passengers with luggage'}
        {selectedVehicle === 'bike' && '🏍️ Bike recommended for Small/Medium boxes (≤ 68×53×43cm) - max 2 boxes'}
        {selectedVehicle === 'van' && '🚐 Van recommended for Large boxes, furniture, or multiple items'}
        {selectedVehicle === 'van+trailer' && '🚛 Van + Trailer recommended for large items, house moves, or bulk furniture (3+ items)'}
      </div>
    </div>
  );
};

const CustomerDashboard = ({ userId, customerName }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [geocodingOrigin, setGeocodingOrigin] = useState(false);
  const [geocodingDest, setGeocodingDest] = useState(false);
  const [originCoordinates, setOriginCoordinates] = useState(null);
  const [destCoordinates, setDestCoordinates] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const mapContainerRef = useRef(null);

  // --- Vehicle selection state ---
  const [selectedVehicle, setSelectedVehicle] = useState('van');

  // --- Live driver location (Socket.io) ---
  const [liveDriverLocation, setLiveDriverLocation] = useState(null);
  const socketRef = useRef(null);

  const { locationStatus, customerLocation, requestLocation } = useCustomerLocation(userId, activeJob?.id);

  // --- Autocomplete state ---
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionType, setActiveSuggestionType] = useState(null);
  const searchTimeoutRef = useRef(null);

  const [jobForm, setJobForm] = useState({
    pickup_address: '', dropoff_address: '',
    pickup_lat: '', pickup_lng: '', dropoff_lat: '', dropoff_lng: '',
    inventory: { 
      boxes: 0, 
      box_size: 'small',
      fridge_double_door: 0, 
      washing_machine: 0, 
      queen_bed: 0, 
      passenger_count: 0 
    },
    has_trailer: false, special_instructions: '',
  });

  // --- Autocomplete functions ---
  const searchAddress = async (query, type) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1&countrycodes=za`;
      const res = await fetch(url, { headers: { 'User-Agent': 'AndromedaLogistics/1.0' } });
      const data = await res.json();
      setAddressSuggestions(data);
      setShowSuggestions(true);
      setActiveSuggestionType(type);
    } catch (err) {
      console.error('Address search error:', err);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectAddressSuggestion = (suggestion, type) => {
    const address = suggestion.display_name;
    const lat = parseFloat(suggestion.lat);
    const lng = parseFloat(suggestion.lon);
    if (type === 'pickup') {
      setJobForm(prev => ({ ...prev, pickup_address: address, pickup_lat: lat.toString(), pickup_lng: lng.toString() }));
      setOriginCoordinates({ lat, lng });
    } else {
      setJobForm(prev => ({ ...prev, dropoff_address: address, dropoff_lat: lat.toString(), dropoff_lng: lng.toString() }));
      setDestCoordinates({ lat, lng });
    }
    setAddressSuggestions([]);
    setShowSuggestions(false);
    setActiveSuggestionType(null);
  };

  const handlePickupInputChange = (e) => {
    const value = e.target.value;
    setJobForm(prev => ({ ...prev, pickup_address: value }));
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchAddress(value, 'pickup'), 300);
  };

  const handleDeliveryInputChange = (e) => {
    const value = e.target.value;
    setJobForm(prev => ({ ...prev, dropoff_address: value }));
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchAddress(value, 'dropoff'), 300);
  };

  const fillPickupFromCoords = useCallback(async (coords) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}`);
      const data = await res.json();
      const address = data.display_name || `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
      setJobForm(prev => ({ ...prev, pickup_address: address, pickup_lat: coords.lat.toString(), pickup_lng: coords.lng.toString() }));
      setOriginCoordinates(coords);
    } catch (err) {
      console.error('Reverse geocoding error:', err);
      setJobForm(prev => ({ ...prev, pickup_address: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`, pickup_lat: coords.lat.toString(), pickup_lng: coords.lng.toString() }));
      setOriginCoordinates(coords);
    }
  }, []);

  const geocodeAddress = async (address) => {
    if (!address?.trim()) return null;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`, { headers: { 'User-Agent': 'AndromedaLogistics/1.0' } });
      const data = await res.json();
      if (data?.length > 0) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lng) };
      return null;
    } catch { return null; }
  };

  const handlePickupChange = useCallback(async (address) => {
    setJobForm(prev => ({ ...prev, pickup_address: address }));
    if (!address?.trim()) return;
    setGeocodingOrigin(true);
    const coords = await geocodeAddress(address);
    if (coords) {
      setOriginCoordinates(coords);
      setJobForm(prev => ({ ...prev, pickup_lat: coords.lat.toString(), pickup_lng: coords.lng.toString() }));
    }
    setGeocodingOrigin(false);
  }, []);

  const handleDeliveryChange = useCallback(async (address) => {
    setJobForm(prev => ({ ...prev, dropoff_address: address }));
    if (!address?.trim()) return;
    setGeocodingDest(true);
    const coords = await geocodeAddress(address);
    if (coords) {
      setDestCoordinates(coords);
      setJobForm(prev => ({ ...prev, dropoff_lat: coords.lat.toString(), dropoff_lng: coords.lng.toString() }));
    }
    setGeocodingDest(false);
  }, []);

  const debouncedPickup = useDebounce(handlePickupChange, 1000);
  const debouncedDelivery = useDebounce(handleDeliveryChange, 1000);

  const openBookingModal = () => {
    setShowCreateModal(true);
    if (locationStatus === 'granted' && customerLocation) {
      fillPickupFromCoords(customerLocation);
    }
  };

  const fetchJobs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found – customer not logged in');
        setJobs([]);
        setActiveJob(null);
        setLoading(false);
        return;
      }
      const headers = { 'Authorization': `Bearer ${token}` };
      const [histRes, activeRes] = await Promise.all([
        fetch(`${API_BASE}/jobs/customer/${userId}/history`, { headers }),
        fetch(`${API_BASE}/jobs/customer/${userId}/active`, { headers }),
      ]);
      const histData = await histRes.json();
      const activeData = await activeRes.json();
      setJobs(histData && Array.isArray(histData.bookings) ? histData.bookings : []);
      setActiveJob(activeData.success && activeData.job ? activeData.job : null);
      setLoading(false);
    } catch (err) {
      console.error('Fetch error:', err);
      setJobs([]);
      setActiveJob(null);
      setLoading(false);
    }
  }, [userId]);

  // ==================== SMART VEHICLE SELECTION LOGIC WITH BOX SIZE ====================

// ==================== SMART VEHICLE SELECTION LOGIC WITH BOX SIZE ====================
const updateInventory = (field, value) => {
  // Update the inventory state
  setJobForm(prev => ({
    ...prev,
    inventory: { ...prev.inventory, [field]: value }
  }));

  // Get current inventory values
  const boxes = field === 'boxes' ? value : jobForm.inventory.boxes;
  const boxSize = field === 'box_size' ? value : jobForm.inventory.box_size;
  const fridge = field === 'fridge_double_door' ? value : jobForm.inventory.fridge_double_door;
  const washingMachine = field === 'washing_machine' ? value : jobForm.inventory.washing_machine;
  const queenBed = field === 'queen_bed' ? value : jobForm.inventory.queen_bed;
  const passengers = field === 'passenger_count' ? value : jobForm.inventory.passenger_count;

  // Count items
  const totalItems = boxes + fridge + washingMachine + queenBed;
  const furnitureItems = fridge + washingMachine + queenBed;
  const hasFurniture = fridge > 0 || washingMachine > 0 || queenBed > 0;
  const hasPassengers = passengers > 0;
  const hasCargo = totalItems > 0;

  const hasLargeBoxes = boxSize === 'large' && boxes > 0;
  const hasLargeItems = hasLargeBoxes || fridge > 0 || washingMachine > 0 || queenBed > 0;

  const hasMoreThanTwo = fridge > 2 || washingMachine > 2 || queenBed > 2;
  const hasMultipleFurniture = furnitureItems >= 3;

  // ✅ NEW: Check if bike is eligible based on box size and quantity
  const isBikeEligible = () => {
    // Bike can only carry:
    // - 1-2 small boxes, OR
    // - 1 medium box
    // - No large boxes
    // - No furniture
    // - No passengers
    // - At least 1 box
    
    if (hasPassengers) return false;
    if (hasFurniture) return false;
    if (boxSize === 'large') return false;
    if (boxSize === 'medium' && boxes > 1) return false;
    if (boxSize === 'small' && boxes > 2) return false;
    if (boxes === 0) return false;
    return true;
  };

  const hasTooManyBoxes = (boxSize === 'small' && boxes > 2) || (boxSize === 'medium' && boxes > 1);

  let suggestedVehicle = 'van';

  // Rule 1: Passengers only (no cargo) → Private Car
  if (hasPassengers && !hasCargo) {
    suggestedVehicle = 'car';
  }
  // Rule 2: Passengers + small luggage (1-2 boxes, small/medium) → Passenger + Cargo
  else if (hasPassengers && boxes > 0 && boxes <= 2 && !hasLargeBoxes && !hasFurniture) {
    suggestedVehicle = 'passenger+cargo';
  }
  // Rule 3: Passengers + large boxes or more items → Van
  else if (hasPassengers && (boxes > 2 || hasLargeBoxes || hasFurniture)) {
    suggestedVehicle = 'van';
  }
  // ✅ Rule 4: Check if bike is eligible (2 small boxes OR 1 medium box)
  else if (isBikeEligible()) {
    suggestedVehicle = 'bike';
  }
  // Rule 5: Too many boxes for bike → Van
  else if (hasTooManyBoxes && !hasFurniture && !hasPassengers) {
    suggestedVehicle = 'van';
  }
  // Rule 6: Large boxes (any) → Van
  else if (hasLargeBoxes && !hasFurniture && !hasPassengers) {
    suggestedVehicle = 'van';
  }
  // Rule 7: 3+ boxes (any size) → Van
  else if (boxes > 2 && !hasFurniture && !hasPassengers) {
    suggestedVehicle = 'van';
  }
  // Rule 8: Furniture items → Van or Van + Trailer
  else if (hasFurniture) {
    if (hasMultipleFurniture || hasMoreThanTwo || fridge > 0 || queenBed > 0) {
      suggestedVehicle = 'van+trailer';
    } else {
      suggestedVehicle = 'van';
    }
  }
  // Rule 9: No items → default to Van
  else if (totalItems === 0 && !hasPassengers) {
    suggestedVehicle = 'van';
  }

  setSelectedVehicle(suggestedVehicle);
};




  // ==================== SOCKET.IO FOR REAL‑TIME DRIVER LOCATION ====================
  useEffect(() => {
    const socket = io('/');
    socketRef.current = socket;
    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    if (!activeJob?.driver_id) {
      setLiveDriverLocation(null);
      return;
    }
    const socket = socketRef.current;
    if (!socket) return;
    const driverId = activeJob.driver_id;
    const eventName = `location_update_${driverId}`;
    const handleLocationUpdate = (coords) => {
      console.log('📍 Driver location update received:', coords);
      setLiveDriverLocation(coords);
    };
    socket.on(eventName, handleLocationUpdate);
    return () => {
      socket.off(eventName, handleLocationUpdate);
    };
  }, [activeJob?.driver_id]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await mapContainerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch { setIsFullscreen(f => !f); }
  };

  const createJob = async () => {
    if (!jobForm.pickup_address || !jobForm.dropoff_address) { alert('Please enter both addresses.'); return; }
    if (!jobForm.pickup_lat || !jobForm.pickup_lng) { alert('Please wait for pickup coordinates to load.'); return; }
    if (!jobForm.dropoff_lat || !jobForm.dropoff_lng) { alert('Please wait for delivery coordinates to load.'); return; }

    const inventoryToSend = {};
    Object.entries(jobForm.inventory).forEach(([k, v]) => { 
      if (v > 0) inventoryToSend[k] = v; 
    });
    
    // ✅ Add box_size to inventory (even if no boxes)
    inventoryToSend.box_size = jobForm.inventory.box_size || 'medium';

    // ✅ Create a separate payload for payment (remove box_size if needed)
    const inventoryForPayment = { ...inventoryToSend };
    // Keep box_size in inventory - Paystack will store it as metadata

    const jobPayload = {
      customer_id: userId,
      pickup_address: jobForm.pickup_address,
      dropoff_address: jobForm.dropoff_address,
      inventory: inventoryToSend,
      pickup_lat: parseFloat(jobForm.pickup_lat),
      pickup_lng: parseFloat(jobForm.pickup_lng),
      dropoff_lat: parseFloat(jobForm.dropoff_lat),
      dropoff_lng: parseFloat(jobForm.dropoff_lng),
      vehicle_type: selectedVehicle === 'bike' ? 'bike' : 
                    selectedVehicle === 'car' ? 'Private Car' :
                    selectedVehicle === 'passenger+cargo' ? 'Passenger + Cargo' :
                    selectedVehicle === 'van+trailer' ? 'Van + Trailer' : 'Van',
      has_trailer: selectedVehicle === 'van+trailer',
      special_instructions: jobForm.special_instructions,
      price_cents: null,
      passenger_count: jobForm.inventory.passenger_count || 0,
    };

    console.log('📦 Job Payload:', jobPayload);

    sessionStorage.setItem('pendingJob', JSON.stringify(jobPayload));

    const customerEmail = localStorage.getItem('userEmail') || 'customer@example.com';
    setIsPaying(true);
    setPaymentError('');

    const token = localStorage.getItem('token');
    if (!token) {
      setPaymentError('Please log in again.');
      setIsPaying(false);
      alert('Session expired – please log in again.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/jobs/initialize-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...jobPayload,
          customer_email: customerEmail,
        }),
      });
      
      const data = await res.json();
      console.log('💳 Payment Response:', data);
      
      if (data.success && data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setPaymentError(data.error || 'Failed to initiate payment');
        sessionStorage.removeItem('pendingJob');
        alert('Payment initiation failed. Please try again.');
      }
    } catch (err) {
      console.error('Payment init error:', err);
      setPaymentError('Network error. Please try again.');
      sessionStorage.removeItem('pendingJob');
      alert('Network error. Please try again.');
    } finally {
      setIsPaying(false);
    }
  };

  const s = {
    page: { minHeight: '100vh', backgroundColor: '#111827', color: 'white', padding: '20px', fontFamily: 'sans-serif' },
    card: { backgroundColor: '#1f2937', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
    activeCard: { backgroundColor: '#1f2937', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '2px solid #eab308' },
    label: { color: '#9ca3af', fontSize: '12px', marginBottom: '2px' },
    value: { fontWeight: 'bold' },
    input: { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white', boxSizing: 'border-box' },
    yellowBtn: { backgroundColor: '#eab308', color: 'black', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
  };

  if (loading) return <div style={{ color: 'white', padding: '20px' }}>Loading your dashboard...</div>;

  // Live driver location takes precedence over polled `current_lat`/`lng`
  const driverLatLive = liveDriverLocation?.lat ?? activeJob?.current_lat;
  const driverLngLive = liveDriverLocation?.lng ?? activeJob?.current_lng;

  return (
    <div style={s.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h1 style={{ color: '#eab308', fontSize: '28px', margin: 0 }}>VTA</h1>
          <p style={{ color: '#9ca3af', margin: '4px 0 0' }}>Welcome back, {customerName || 'Customer'}!</p>
        </div>
        <button onClick={openBookingModal} style={{ ...s.yellowBtn, padding: '12px 24px', fontSize: '14px' }}>
          + BOOK NEW DELIVERY
        </button>
      </div>

      <LocationPermissionBanner status={locationStatus} onRequest={requestLocation} />

      {activeJob && (
        <>
          <div style={s.activeCard}>
            <h2 style={{ color: '#eab308', marginBottom: '15px' }}>📦 ACTIVE DELIVERY</h2>
            <div style={s.grid2}>
              <div>
                <div style={s.label}>🟢 PICKUP</div>
                <div style={s.value}>{activeJob.pickup_address}</div>
              </div>
              <div>
                <div style={s.label}>📍 DROP-OFF</div>
                <div style={s.value}>{activeJob.dropoff_address}</div>
              </div>
              <div>
                <div style={s.label}>💰 AMOUNT</div>
                <div style={{ fontWeight: 'bold', color: '#eab308' }}>R{(activeJob.price_cents / 100).toFixed(2)}</div>
              </div>
              <div>
                <div style={s.label}>STATUS</div>
                <span style={{ backgroundColor: activeJob.status === 'delivered' ? '#10b981' : '#eab308', color: 'black', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                  {activeJob.status.toUpperCase()}
                </span>
              </div>
            </div>

            <div style={{ marginTop: '16px', padding: '14px', backgroundColor: '#111827', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>🔐 SAFETY PIN — give this to your driver</div>
                <div style={{ fontFamily: 'monospace', fontSize: '32px', fontWeight: 'bold', color: '#eab308', letterSpacing: '8px' }}>
                  {activeJob.verification_pin}
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', maxWidth: '200px' }}>
                Driver enters this PIN to confirm pickup.
              </div>
            </div>

            <DriverStatusBar activeJob={activeJob} />

            {activeJob.driver_name && (
              <div style={{ marginTop: '16px', padding: '14px', backgroundColor: '#111827', borderRadius: '8px', borderTop: '1px solid #eab308' }}>
                <div style={{ fontSize: '14px', color: '#eab308', marginBottom: '10px', fontWeight: 'bold' }}>🚚 Assigned Driver</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 2 }}>
                    <div><strong>Name:</strong> {activeJob.driver_name}</div>
                    {activeJob.vehicle_brand && <div><strong>Vehicle:</strong> {activeJob.vehicle_brand}{activeJob.vehicle_color ? ` (${activeJob.vehicle_color})` : ''}</div>}
                    {activeJob.vehicle_plate_number && <div><strong>Plate No:</strong> {activeJob.vehicle_plate_number}</div>}
                    {activeJob.driver_vehicle_type && <div><strong>Type:</strong> {activeJob.driver_vehicle_type}</div>}
                  </div>
                  {activeJob.driver_phone && (
                    <a href={`tel:${activeJob.driver_phone}`} style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      📞 Call Driver
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          <div
            ref={mapContainerRef}
            style={{
              backgroundColor: '#1f2937',
              borderRadius: isFullscreen ? '0' : '12px',
              padding: isFullscreen ? '0' : '20px',
              marginBottom: '20px',
              overflow: 'hidden',
              position: isFullscreen ? 'fixed' : 'relative',
              top: isFullscreen ? 0 : 'auto',
              left: isFullscreen ? 0 : 'auto',
              height: isFullscreen ? '100vh' : 'auto',
              width: isFullscreen ? '100vw' : 'auto',
              zIndex: isFullscreen ? 9999 : 1,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: isFullscreen ? '10px 20px' : '0', backgroundColor: isFullscreen ? '#1f2937' : 'transparent' }}>
              <h2 style={{ color: '#eab308', margin: 0 }}>🗺️ Track Your Delivery</h2>
              <button onClick={toggleFullscreen} style={{ backgroundColor: '#374151', color: '#eab308', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                {isFullscreen ? '✕ EXIT' : '⛶ FULLSCREEN'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap', padding: isFullscreen ? '0 20px' : '0' }}>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>🟢 Your pickup</span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>📍 Drop-off</span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>🚚 Driver</span>
              <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 'bold' }}>━ Driver to you</span>
              <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold' }}>━ To drop-off</span>
            </div>

            <div style={{ height: isFullscreen ? 'calc(100vh - 100px)' : '480px', width: '100%', position: 'relative', zIndex: 1, touchAction: 'pan-x pan-y' }}>
              <TrackingMap
                pickupLat={activeJob.pickup_lat}
                pickupLng={activeJob.pickup_lng}
                dropoffLat={activeJob.dropoff_lat}
                dropoffLng={activeJob.dropoff_lng}
                driverLat={driverLatLive}
                driverLng={driverLngLive}
                driverName={activeJob.driver_name || 'Driver'}
                status={activeJob.status}
                customerLat={customerLocation?.lat}
                customerLng={customerLocation?.lng}
              />
            </div>

            {!isFullscreen && (
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                {activeJob.status === 'picked_up'
                  ? '📦 Your goods are on the way to the drop-off location.'
                  : activeJob.status === 'assigned'
                    ? '🚚 Driver is heading to your pickup location.'
                    : activeJob.status === 'delivered'
                      ? '✅ Delivery complete!'
                      : '⏳ Waiting for a driver to accept your job.'}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <Chat jobId={activeJob.id} currentUserId={userId} currentUserName={customerName} userRole="customer" />
          </div>
        </>
      )}

      <div style={s.card}>
        <h2 style={{ color: '#eab308', marginBottom: '20px' }}>📋 Delivery History</h2>
        {jobs.length === 0 ? (
          <p style={{ color: '#9ca3af', textAlign: 'center' }}>No deliveries yet. Click "+ BOOK NEW DELIVERY" to get started!</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #374151' }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 'bold', fontSize: '12px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 'bold', fontSize: '12px' }}>From</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 'bold', fontSize: '12px' }}>To</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 'bold', fontSize: '12px' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 'bold', fontSize: '12px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 'bold', fontSize: '12px' }}>PIN</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ padding: '12px' }}>{new Date(job.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '12px', color: '#d1d5db' }}>{job.pickup_address?.substring(0, 28)}…</td>
                    <td style={{ padding: '12px', color: '#d1d5db' }}>{job.dropoff_address?.substring(0, 28)}…</td>
                    <td style={{ padding: '12px', color: '#eab308', fontWeight: 'bold' }}>R{(job.price_cents / 100).toFixed(2)}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ backgroundColor: job.status === 'delivered' ? '#064e3b' : job.status === 'picked_up' ? '#1e3a5f' : '#374151', color: job.status === 'delivered' ? '#34d399' : job.status === 'picked_up' ? '#60a5fa' : '#9ca3af', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                        {job.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '18px', color: '#eab308', letterSpacing: '4px' }}>
                      {job.verification_pin || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '30px', width: '560px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', border: '2px solid #eab308' }}>
            <h2 style={{ color: '#eab308', marginBottom: '20px' }}>📦 Book New Delivery</h2>

            {/* PICKUP ADDRESS */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '6px' }}>🟢 PICKUP ADDRESS *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="e.g., 1 Main St, Cape Town"
                  value={jobForm.pickup_address}
                  onChange={handlePickupInputChange}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onFocus={() => {
                    if (jobForm.pickup_address.length >= 3 && addressSuggestions.length > 0) setShowSuggestions(true);
                  }}
                  style={s.input}
                />
                {showSuggestions && activeSuggestionType === 'pickup' && addressSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto', zIndex: 10 }}>
                    {addressSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        onClick={() => selectAddressSuggestion(suggestion, 'pickup')}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #374151', color: '#d1d5db', fontSize: '13px' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#374151'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        {suggestion.display_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {geocodingOrigin && <div style={{ fontSize: '11px', color: '#eab308', marginTop: '4px' }}>⏳ Finding coordinates...</div>}
              {originCoordinates && !geocodingOrigin && <div style={{ fontSize: '11px', color: '#10b981', marginTop: '4px' }}>✅ Location confirmed</div>}

              {!originCoordinates && locationStatus !== 'denied' && (
                <button
                  onClick={async () => {
                    if (locationStatus !== 'granted') {
                      requestLocation();
                      const waitForLocation = setInterval(() => {
                        if (customerLocation) {
                          fillPickupFromCoords(customerLocation);
                          clearInterval(waitForLocation);
                        }
                      }, 500);
                      setTimeout(() => clearInterval(waitForLocation), 10000);
                    } else if (customerLocation) {
                      fillPickupFromCoords(customerLocation);
                    } else {
                      navigator.geolocation.getCurrentPosition(
                        (pos) => fillPickupFromCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                        () => alert('Unable to get location. Please check your GPS.'),
                        { enableHighAccuracy: true, timeout: 10000 }
                      );
                    }
                  }}
                  disabled={locationStatus === 'requesting'}
                  style={{
                    marginTop: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    color: locationStatus === 'requesting' ? '#9ca3af' : '#3b82f6',
                    background: 'none',
                    border: '1px solid',
                    borderColor: locationStatus === 'requesting' ? '#4b5563' : '#3b82f6',
                    borderRadius: '6px',
                    cursor: locationStatus === 'requesting' ? 'default' : 'pointer',
                    padding: '5px 10px',
                  }}
                >
                  {locationStatus === 'requesting' ? '⏳ Getting location…' : '📍 Use my current location'}
                </button>
              )}
              {locationStatus === 'denied' && !originCoordinates && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#ef4444' }}>
                  📵 Location blocked — enable it in browser settings or type your address above.
                </div>
              )}
            </div>

            {/* DELIVERY ADDRESS */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '6px' }}>📍 DELIVERY ADDRESS *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="e.g., 25 Main St, Johannesburg"
                  value={jobForm.dropoff_address}
                  onChange={handleDeliveryInputChange}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onFocus={() => {
                    if (jobForm.dropoff_address.length >= 3 && addressSuggestions.length > 0) setShowSuggestions(true);
                  }}
                  style={s.input}
                />
                {showSuggestions && activeSuggestionType === 'dropoff' && addressSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto', zIndex: 10 }}>
                    {addressSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        onClick={() => selectAddressSuggestion(suggestion, 'dropoff')}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #374151', color: '#d1d5db', fontSize: '13px' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#374151'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        {suggestion.display_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {geocodingDest && <div style={{ fontSize: '11px', color: '#eab308', marginTop: '4px' }}>⏳ Finding coordinates...</div>}
              {destCoordinates && !geocodingDest && <div style={{ fontSize: '11px', color: '#10b981', marginTop: '4px' }}>✅ Location confirmed</div>}
            </div>

            {/* VEHICLE SELECTION */}
            <VehicleSelection selectedVehicle={selectedVehicle} onSelect={setSelectedVehicle} />

            {/* Passenger Count */}
            <div style={{ marginBottom: '15px', borderTop: '1px solid #374151', paddingTop: '14px' }}>
              <div style={{ color: '#eab308', fontSize: '13px', fontWeight: 'bold', marginBottom: '10px' }}>
                👤 Passengers
              </div>
              <InventoryRow 
                label="Number of Passengers" 
                field="passenger_count" 
                value={jobForm.inventory.passenger_count} 
                onChange={updateInventory} 
              />
            </div>

            {/* Inventory with Box Size */}
            <div style={{ marginBottom: '15px', borderTop: '1px solid #374151', paddingTop: '14px' }}>
              <div style={{ color: '#eab308', fontSize: '13px', fontWeight: 'bold', marginBottom: '10px' }}>📦 Inventory</div>
              
              {/* Box Size Selection */}
              <BoxSizeSelection 
                selectedSize={jobForm.inventory.box_size || 'small'} 
                onSelect={(size) => updateInventory('box_size', size)} 
              />
              
              <InventoryRow label="Boxes" field="boxes" value={jobForm.inventory.boxes} onChange={updateInventory} />
              <InventoryRow label="Fridge (Double Door)" field="fridge_double_door" value={jobForm.inventory.fridge_double_door} onChange={updateInventory} />
              <InventoryRow label="Washing Machine" field="washing_machine" value={jobForm.inventory.washing_machine} onChange={updateInventory} />
              <InventoryRow label="Queen Bed" field="queen_bed" value={jobForm.inventory.queen_bed} onChange={updateInventory} />
            </div>

            {/* Trailer toggle - only show for van+trailer */}
            {selectedVehicle === 'van+trailer' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={jobForm.has_trailer} onChange={(e) => setJobForm(prev => ({ ...prev, has_trailer: e.target.checked }))} style={{ width: '16px', height: '16px' }} />
                  <span style={{ color: '#d1d5db', fontSize: '13px' }}>🚚 This delivery requires a trailer</span>
                </label>
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '6px' }}>SPECIAL INSTRUCTIONS</label>
              <textarea rows="2" placeholder="e.g., Fragile items, call before delivery" value={jobForm.special_instructions} onChange={(e) => setJobForm(prev => ({ ...prev, special_instructions: e.target.value }))} style={{ ...s.input, resize: 'vertical' }} />
            </div>

            {paymentError && <div style={{ color: '#ef4444', fontSize: '12px', textAlign: 'center', marginBottom: '10px' }}>{paymentError}</div>}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowCreateModal(false)} style={{ flex: 1, padding: '12px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                CANCEL
              </button>
              <button
                onClick={createJob}
                disabled={!originCoordinates || !destCoordinates || isPaying}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  backgroundColor: (!originCoordinates || !destCoordinates || isPaying) ? '#4b5563' : '#eab308',
                  color: (!originCoordinates || !destCoordinates || isPaying) ? '#9ca3af' : 'black',
                  cursor: (!originCoordinates || !destCoordinates || isPaying) ? 'not-allowed' : 'pointer',
                }}
              >
                {isPaying ? '⏳ REDIRECTING TO PAYSTACK...' : '🚚 BOOK DELIVERY'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
